import type { ExperimentalQueries } from '../__types__/ExperimentalQueries';
import { MediaType } from '../MediaType';

export const experimentalTeamCalendars: ExperimentalQueries['experimentalTeamCalendars'] = (
	_,
	{ viewingSpaceKey, include, calendarContext },
	{ network },
) => {
	const params = new URLSearchParams();
	params.append('calendarContext', calendarContext);
	if (viewingSpaceKey) {
		params.append('viewingSpaceKey', viewingSpaceKey);
	}
	if (include) {
		include.forEach((calendarId: string) => {
			params.append('include', calendarId);
		});
	}

	return network
		.cfetch(`/wiki/rest/calendar-services/1.0/calendar/subcalendars.json?${params}`, {
			method: 'GET',
			headers: {
				'Content-Type': MediaType.JSON,
			},
		})
		.then((res) => res.json());
};

export const experimentalTeamCalendarEvents: ExperimentalQueries['experimentalTeamCalendarEvents'] =
	(_, { subCalendarId, userTimeZoneId, start, end }, { network }) => {
		if (!subCalendarId || !userTimeZoneId || !start || !end) {
			return Promise.reject(new Error('Check your arguments'));
		}

		return network
			.cfetch(
				`/wiki/rest/calendar-services/1.0/calendar/events.json?subCalendarId=${subCalendarId}&userTimeZoneId=${userTimeZoneId}&start=${start}&end=${end}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': MediaType.JSON,
					},
				},
			)
			.then((res) => {
				return res.json() || [];
			});
	};

export const experimentalTeamCalendarsSubscribe: ExperimentalQueries['experimentalTeamCalendarsSubscribe'] =
	(_, { calendarId }, { network }) => {
		if (!calendarId) {
			return Promise.reject(new Error('calendarId is missing.'));
		}
		return (
			network
				.cfetch(`/wiki/rest/calendar-services/1.0/calendar/subcalendar/privateurl/${calendarId}`, {
					method: 'GET',
					headers: {
						'Content-Type': MediaType.JSON,
					},
				})
				.then((res) => res.text())
				.then((data) => {
					return {
						url: data,
					};
				})
				// TODO FIXME the object shape returned here does not correspond to experimental schema type definitions
				// I'll `any` it right now, but this needs a proper fix
				.catch(
					(reason) =>
						({
							statusCode: reason instanceof network.BadStatusError ? reason.response.status : 404,
						}) as any,
				)
		);
	};

export const experimentalTeamCalendarWatchStatus: ExperimentalQueries['experimentalTeamCalendarWatchStatus'] =
	(_, { subCalendarIds }, { network }) => {
		if (!subCalendarIds || !subCalendarIds.length) {
			return Promise.reject(new Error('Check your arguments'));
		}

		const subCalQueryString = subCalendarIds.reduce((acc, scId) => `${acc}&include=${scId}`, '');

		return network
			.cfetch(
				`/wiki/rest/calendar-services/1.0/calendar/subcalendars/watching/status.json?${subCalQueryString}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': MediaType.JSON,
					},
				},
			)
			.then((res) => res.json())
			.then((data = []) => {
				if (data.success) {
					return data.payload;
				} else {
					return Promise.reject(new Error('Request failed.'));
				}
			})
			.catch((reason) => ({
				statusCode: reason instanceof network.BadStatusError ? reason.response.status : 404,
			}));
	};

export const experimentalTeamCalendarsFormatDate: ExperimentalQueries['experimentalTeamCalendarsFormatDate'] =
	(_, { pattern, dateTimes }, { network }) => {
		// dateTimeFormat : ddMMyyyyHHmm ie "140720200000"
		const params = new URLSearchParams();
		dateTimes.forEach((dateTime: string) => {
			params.append('date', dateTime);
		});
		params.append('pattern', pattern);

		return network
			.cfetch(`/wiki/rest/calendar-services/1.0/calendar/util/format/date.json?${params}`, {
				method: 'GET',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': MediaType.JSON,
				},
			})
			.then((res) => res.json())
			.then((dates: string[]) => {
				return {
					dates,
				};
			})
			.catch(
				(reason) =>
					({
						// TODO FIXME the object shape returned here does not correspond to experimental schema type definitions
						// I'll `any` it right now, but this needs a proper fix
						statusCode: reason instanceof network.BadStatusError ? reason.response.status : 404,
					}) as any,
			);
	};

export const experimentalTeamCalendarTimezonesQuery: ExperimentalQueries['experimentalTeamCalendarTimezonesQuery'] =
	(_, __, { network }) => {
		return network
			.cfetch(`/wiki/rest/calendar-services/1.0/timezones/calendar`, {
				method: 'GET',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': MediaType.JSON,
				},
			})
			.then((res) => res.json());
	};

export const experimentalTeamCalendarsSearch: ExperimentalQueries['experimentalTeamCalendarsSearch'] =
	(
		_,
		{ term, startIndex, pageSize, isPopular, showSubCalendarsInView, showSubscriberCount },
		{ network },
	) => {
		let url;
		let params;

		if (isPopular) {
			params = new URLSearchParams({
				startIndex: String(startIndex),
				pageSize: String(pageSize),
			});
			url = `/wiki/rest/calendar-services/1.0/calendar/search/subcalendars/popular?${params}`;
		} else {
			// Special handling some characters which can cause search errors.
			const specialCharacters = [
				{ char: '<', value: '&#60;' },
				{ char: '=', value: '&#61;' },
				{ char: '>', value: '&#62;' },
				{ char: '@', value: '&#64;' },
			];
			specialCharacters.forEach((item) => {
				if (term?.includes(item.char)) {
					const replaceRegex = new RegExp(item.char, 'g');
					term = term.replace(replaceRegex, item.value);
				}
			});
			params = new URLSearchParams({
				term: term || '',
				startIndex: String(startIndex),
				pageSize: String(pageSize),
				showSubCalendarsInView: String(showSubCalendarsInView),
				showSubscriberCount: String(showSubscriberCount),
			});
			url = `/wiki/rest/calendar-services/1.0/calendar/search/subcalendars.json?${params}`;
		}

		return network
			.cfetch(url, {
				method: 'GET',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': MediaType.JSON,
				},
			})
			.then((res) => res.json());
	};

export const experimentalTeamCalendarJiraLinksQuery: ExperimentalQueries['experimentalTeamCalendarJiraLinksQuery'] =
	(_, __, { network }) => {
		return network
			.cfetch(`/wiki/rest/calendar-services/1.0/calendar/jira/jiraLinks`, {
				method: 'GET',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': MediaType.JSON,
				},
			})
			.then((res) => res.json());
	};

export const experimentalTeamCalendarJiraOptionsQuery: ExperimentalQueries['experimentalTeamCalendarJiraOptionsQuery'] =
	(_, { serverId }, { network }) => {
		return network
			.cfetch(`/wiki/rest/calendar-services/1.0/calendar/jira/${serverId}/query/options.json`, {
				method: 'GET',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': MediaType.JSON,
				},
			})
			.then((res) => res.json());
	};

export const experimentalTeamCalendarJiraDateFieldsQuery: ExperimentalQueries['experimentalTeamCalendarJiraDateFieldsQuery'] =
	(_, { serverId, displayField, fieldParam, queryParam }, { network }) => {
		let params = '';
		//this maybe null depending on which type of Jira event
		if (displayField) {
			params = `/${displayField}`;
		}
		//fieldParam will always be present and can either be project/filter ID. For jql, it is hardcoded as jql
		params += `/${fieldParam}.json`;
		//this will be present only for JQL types
		if (queryParam) {
			params += `?query=${queryParam}`;
		}
		const rootUrl = `/wiki/rest/calendar-services/1.0/calendar/jira/${serverId}/date-fields${params}`;
		return network
			.cfetch(rootUrl, {
				method: 'GET',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': MediaType.JSON,
				},
			})
			.then((res) => res.json());
	};

export const experimentalTeamCalendarViewPreference: ExperimentalQueries['experimentalTeamCalendarViewPreference'] =
	(_, __, { network }) => {
		return network
			.cfetch(`/wiki/rest/calendar-services/1.0/calendar/preferences.json`, {
				method: 'GET',
				headers: {
					Accept: MediaType.JSON,
					'Content-Type': MediaType.JSON,
				},
			})
			.then((res) => res.json())
			.catch(
				(reason) =>
					({
						statusCode: reason instanceof network.BadStatusError ? reason.response.status : 404,
					}) as any,
			);
	};

export const experimentalTeamCalendarSubscriberCount: ExperimentalQueries['experimentalTeamCalendarSubscriberCount'] =
	(_, { calendarId, calendarContext, viewingSpaceKey }, { network }) => {
		return network
			.cfetch(
				`/wiki/rest/calendar-services/1.0/calendar/subcalendars/links.json?subCalendarId=${calendarId}&calendarContext=${calendarContext}&viewingSpaceKey=${viewingSpaceKey}`,
				{
					method: 'GET',
					headers: {
						Accept: MediaType.JSON,
						'Content-Type': MediaType.JSON,
					},
				},
			)
			.then((res) => res.json());
	};
